/* animations.js — buckssauce-exact motion engine for the Taabaa theme.
   Stack: GSAP 3.13 + ScrollTrigger + SplitText + CustomEase + Lenis.
   Reveal params are ported verbatim from docs/buckssauce-animation-spec.md.
   Content is visible by default (CSS never hides it); GSAP sets hidden start
   states in JS only after it loads, so a GSAP/SplitText failure can never blank
   the page. Reduced motion (when honoured) resolves reveals instantly and
   disables smooth scroll. */
(function () {
  'use strict';
  var root = document.documentElement;
  var OFF = root.classList.contains('no-anim');
  // Honour the device's reduce-motion preference ONLY when the merchant hasn't
  // forced motion on (force-motion class). Unchanged from the previous engine.
  var REDUCE = !root.classList.contains('force-motion')
    && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!window.gsap || !window.ScrollTrigger) return; // content stays visible
  var gsap = window.gsap;
  var ST = window.ScrollTrigger;
  gsap.registerPlugin(ST);
  if (window.SplitText) gsap.registerPlugin(window.SplitText);
  if (window.CustomEase) {
    gsap.registerPlugin(window.CustomEase);
    try {
      window.CustomEase.create('bigCopy',
        'M0,0 C0.084,0.61 0.1,1.09 0.2,1.1 0.306,1.11 0.295,0.978 0.386,0.978 0.444,0.978 0.477,1 0.519,1 0.619,1 0.888,1 1,1');
    } catch (e) {}
  }
  var SplitText = window.SplitText;

  if (OFF) { ST.refresh(); return; } // animations disabled: leave everything visible

  /* ---- buckssauce exact tables (docs/buckssauce-animation-spec.md §2) ---- */
  var DUR  = { hover: 0.25, fast: 0.3, medium: 0.4, base: 0.5, slow: 0.8, title: 0.48, paragraph: 0.7, bigCopy: 1.25 };
  var STAG = { text: 0.03, base: 0.05, title: 0.028, paragraph: 0.035, bigCopy: 0.035 };
  var EASE = {
    base: 'power2.out',
    copy: 'power3.out',
    backUi: 'back.out(1.2)',
    backLow: 'back.out(1.4)',
    backBase: 'back.out(1.7)',
    bigCopy: (window.CustomEase ? 'bigCopy' : 'power3.out')
  };
  var LEGACY_EASE = 'power3.out';

  /* --------------------------------------------------------------- Lenis */
  // Buckssauce-matched smooth scroll. Defaults (lerp 0.1, smoothWheel) reproduce
  // the source's glide exactly. Touch is left native. ScrollTrigger is driven off
  // Lenis; reveals still use a native IntersectionObserver so they work either way.
  var lenis = null;
  // Smooth-scroll only smooths the mouse wheel (touch is native), yet its rAF loop
  // runs every frame regardless — pure wasted main-thread work on phones. Skip it on
  // touch devices; ScrollTrigger then drives off native scroll (reveals use IO anyway).
  var COARSE = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  if (window.Lenis && !REDUCE && !COARSE) {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    lenis = new window.Lenis({ smoothWheel: true }); // buckssauce: defaults (lerp 0.1)
    lenis.on('scroll', ST.update);
    gsap.ticker.add(function (t) { lenis.raf(t * 1000); });
    gsap.ticker.lagSmoothing(0);
    window.__lenis = lenis;
    try { document.dispatchEvent(new CustomEvent('lenis:ready')); } catch (e) {}
    // Pause smooth scroll whenever an overlay locks the body (cart/menu/search).
    try {
      new MutationObserver(function () {
        if (document.body.style.overflow === 'hidden') lenis.stop(); else lenis.start();
      }).observe(document.body, { attributes: true, attributeFilter: ['style'] });
    } catch (e) {}
  }

  /* ------------------------------------------------ buckssauce builders */
  function splitIgnore(el) {
    return (el.classList.contains('no-br-mobile') && window.innerWidth < 1024) ? 'br' : '';
  }

  var builders = {
    title: function (el, tl) {
      tl = tl || gsap.timeline();
      if (REDUCE || !SplitText) { tl.set(el, { opacity: 1 }); return tl; }
      var s = new SplitText(el, { type: 'lines,words,chars', wordsClass: 'word', linesClass: 'line', charsClass: 'char', ignore: splitIgnore(el) });
      tl.set(s.chars, { opacity: 0, y: 60, scaleX: 0.8, scaleY: 0.5 })
        .set(el, { opacity: 1 }, '<')
        .to(s.chars, { opacity: 1, y: 0, scaleX: 1, scaleY: 1, duration: DUR.title, stagger: STAG.title, ease: EASE.backBase }, '<');
      return tl;
    },
    text: function (el, tl) {
      tl = tl || gsap.timeline();
      if (REDUCE || !SplitText) { tl.set(el, { opacity: 1 }); return tl; }
      var s = new SplitText(el, { type: 'lines', linesClass: 'line', ignore: splitIgnore(el) });
      tl.set(s.lines, { opacity: 0, yPercent: 50 })
        .set(el, { opacity: 1 }, '<')
        .to(s.lines, { opacity: 1, yPercent: 0, duration: DUR.paragraph, stagger: STAG.paragraph, ease: EASE.copy }, '<');
      return tl;
    },
    bigCopy: function (el, tl) {
      tl = tl || gsap.timeline();
      if (REDUCE || !SplitText) { tl.set(el, { opacity: 1 }); return tl; }
      var s = new SplitText(el, { type: 'lines,words,chars', wordsClass: 'word', linesClass: 'line', ignore: splitIgnore(el) });
      tl.set(s.words, { opacity: 0, y: 100, scaleX: 0.5, scaleY: 1.5, rotation: 'random(-30,30)' })
        .set(el, { opacity: 1 }, '<')
        .to(s.words, { opacity: 1, y: 0, scaleX: 1, scaleY: 1, rotation: 0, delay: 0.1, duration: DUR.bigCopy, stagger: STAG.bigCopy, ease: EASE.bigCopy }, '<');
      return tl;
    },
    appear: function (el, tl) {
      tl = tl || gsap.timeline();
      tl.set(el, { opacity: 0 }).to(el, { opacity: 1, duration: DUR.base, ease: EASE.base });
      return tl;
    },
    slideIn: function (el, tl) {
      tl = tl || gsap.timeline();
      tl.set(el, { opacity: 0, y: REDUCE ? 14 : 64 })
        .to(el, { opacity: 1, y: 0, duration: DUR.base, ease: REDUCE ? EASE.base : EASE.backUi });
      return tl;
    },
    scaleUp: function (el, tl) {
      tl = tl || gsap.timeline();
      tl.set(el, { opacity: 0, scale: REDUCE ? 1 : 0 })
        .to(el, { opacity: 1, scale: 1, duration: DUR.base, ease: REDUCE ? EASE.base : EASE.backLow });
      return tl;
    },
    // Group reveal: direct children cascade up+fade (smooth, no bounce). Used on
    // card grids/sliders so a row reveals as one staggered group (Bucks feel).
    stagger: function (el, tl) {
      tl = tl || gsap.timeline();
      var kids = el.children;
      tl.set(el, { opacity: 1 });
      if (REDUCE) { tl.set(kids, { opacity: 1, y: 0 }); return tl; }
      tl.set(kids, { opacity: 0, y: 44 })
        .to(kids, { opacity: 1, y: 0, duration: 0.62, stagger: 0.07, ease: 'power3.out' });
      return tl;
    }
  };

  // attribute suffix -> builder key
  var MAP = { 'title': 'title', 'text': 'text', 'big-copy': 'bigCopy', 'appear': 'appear', 'slide-in': 'slideIn', 'scale-up': 'scaleUp', 'stagger': 'stagger' };

  // IntersectionObserver, fires once. Uses threshold 0 + a bottom rootMargin so
  // it triggers when the element's TOP enters the viewport — independent of the
  // element's height. (A plain threshold:0.25 never fires for very tall elements
  // like a full product grid, leaving every card stuck hidden.)
  function watch(el, play) {
    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { obs.unobserve(el); play(el); obs.disconnect(); }
      });
    }, { root: null, rootMargin: '0px 0px -12% 0px', threshold: 0 });
    obs.observe(el);
  }

  // Entrance curtain: if it's present and about to play, defer the immediate
  // (on-mount) reveals — hero headline etc. — so they play AS the curtain lifts
  // rather than hidden underneath it.
  var curtain = window.__curtain || document.querySelector('[data-curtain]');
  var curtainWillPlay = !!(curtain && curtain.isConnected && curtain.__done);
  var deferred = [];

  // Wire all data-gsap-* in a scope; guard so editor reloads don't double-process.
  // Track on-scroll elements so the safety net can rescue any that never fire.
  var onScrollEls = [];
  function wire(scope) {
    Object.keys(MAP).forEach(function (attr) {
      var build = builders[MAP[attr]];
      scope.querySelectorAll('[data-gsap-' + attr + ']:not([data-gsap-done])').forEach(function (el) {
        el.setAttribute('data-gsap-done', '');
        if (curtainWillPlay) { deferred.push([build, el]); return; }
        build(el);
      });
      scope.querySelectorAll('[data-gsap-' + attr + '-on-scroll]:not([data-gsap-done])').forEach(function (el) {
        el.setAttribute('data-gsap-done', '');
        // Pre-hide immediately (engine confirmed loaded) so the element can't
        // flash its un-revealed state before the IntersectionObserver fires.
        // For 'stagger' the container stays visible; its children are pre-hidden.
        if (attr === 'stagger') { gsap.set(el.children, { opacity: 0 }); }
        else { gsap.set(el, { opacity: 0 }); }
        onScrollEls.push(el);
        watch(el, build);
      });
    });
  }
  wire(document);
  document.addEventListener('shopify:section:load', function (e) { wire(e.target); });

  /* ----------------------- entrance curtain + opening sequence ------------ */
  var headbar = document.querySelector('[data-headbar]');
  function openHeader() {
    if (headbar) gsap.from(headbar, { yPercent: -130, opacity: 0, duration: REDUCE ? 0.4 : 0.7, ease: EASE.copy });
  }
  function playDeferred() {
    curtainWillPlay = false;
    for (var i = 0; i < deferred.length; i++) { try { deferred[i][0](deferred[i][1]); } catch (e) {} }
    deferred.length = 0;
    openHeader();
    ST.refresh();
  }

  if (curtainWillPlay && !REDUCE) {
    if (lenis) lenis.stop();
    try { window.scrollTo(0, 0); } catch (e) {}
    var cWord = curtain.querySelector('[data-curtain-word]');
    var cLogo = curtain.querySelector('.page-curtain__logo');
    var cFloaties = curtain.querySelectorAll('.bx-floaty');
    var ctl = gsap.timeline({
      onComplete: function () { if (curtain.__done) curtain.__done(); if (lenis) lenis.start(); }
    });
    if (cWord && SplitText) {
      var cSplit = new SplitText(cWord, { type: 'chars', charsClass: 'char' });
      ctl.set(cWord, { opacity: 1 })
         .from(cSplit.chars, { opacity: 0, yPercent: 110, scaleY: 0.4, duration: 0.62, stagger: 0.045, ease: EASE.backBase });
    } else {
      ctl.from(cWord || cLogo || curtain, { opacity: 0, y: 40, duration: 0.6, ease: EASE.copy });
    }
    if (cFloaties.length) ctl.from(cFloaties, { opacity: 0, scale: 0, duration: 0.5, stagger: 0.05, ease: EASE.backLow }, '-=0.35');
    ctl.to({}, { duration: 0.4 });                                    // brief hold
    ctl.to(curtain, { yPercent: -100, duration: 0.95, ease: 'power3.inOut' });
    ctl.call(playDeferred, null, '-=0.55');                           // hero reveals begin as the curtain lifts
  } else {
    if (curtainWillPlay && curtain.__done) { curtain.__done(); }       // reduced motion: drop curtain instantly
    playDeferred();                                                    // (no-op deferred list when no curtain)
  }

  /* ---------------- LEGACY handlers (retained for un-migrated sections) ---- */
  // Heading line reveals (mask). Removed once all sections use data-gsap-*.
  function maskLines(el) {
    var inners = [];
    Array.prototype.forEach.call(el.children, function (line) {
      if (line.__masked) { inners.push(line.__inner); return; }
      line.style.overflow = 'hidden';
      line.style.paddingBottom = '0.08em';
      var inner = document.createElement('span');
      inner.style.display = 'block';
      inner.style.willChange = 'transform';
      while (line.firstChild) inner.appendChild(line.firstChild);
      line.appendChild(inner);
      line.__masked = true; line.__inner = inner;
      inners.push(inner);
    });
    return inners;
  }
  gsap.utils.toArray('[data-anim-lines]').forEach(function (el) {
    if (REDUCE) {
      gsap.from(el, { opacity: 0, duration: 0.5, scrollTrigger: { trigger: el, start: 'top 85%', once: true } });
      return;
    }
    var inners = maskLines(el);
    gsap.set(inners, { yPercent: 115 });
    ST.create({
      trigger: el, start: 'top 82%', once: true,
      onEnter: function () { gsap.to(inners, { yPercent: 0, duration: 0.95, ease: LEGACY_EASE, stagger: 0.12 }); }
    });
  });

  var reveals = gsap.utils.toArray('[data-reveal]');
  gsap.set(reveals, { opacity: 0, y: REDUCE ? 14 : 54, scale: REDUCE ? 1 : 0.97 });
  ST.batch(reveals, {
    start: 'top 86%',
    onEnter: function (els) {
      gsap.to(els, { opacity: 1, y: 0, scale: 1, duration: REDUCE ? 0.45 : 0.9, ease: LEGACY_EASE, stagger: 0.1, overwrite: true });
    }
  });

  gsap.utils.toArray('[data-reveal-stagger]').forEach(function (el) {
    var kids = el.children;
    gsap.set(kids, { opacity: 0, y: REDUCE ? 14 : 40 });
    ST.create({
      trigger: el, start: 'top 85%', once: true,
      onEnter: function () { gsap.to(kids, { opacity: 1, y: 0, duration: REDUCE ? 0.4 : 0.7, ease: LEGACY_EASE, stagger: 0.1 }); }
    });
  });

  /* ------------------------------------------ extras (unchanged behavior) */
  var progressBar = document.querySelector('.scroll-progress');
  if (progressBar) {
    gsap.to(progressBar, {
      scaleX: 1, ease: 'none',
      scrollTrigger: { trigger: document.documentElement, start: 0, end: 'max', scrub: 0.3 }
    });
  }

  if (!REDUCE) {
    gsap.utils.toArray('[data-parallax]').forEach(function (el) {
      var speed = parseFloat(el.getAttribute('data-parallax')) || 0.2;
      gsap.to(el, {
        yPercent: -speed * 100, ease: 'none',
        scrollTrigger: { trigger: el.closest('section') || el, start: 'top bottom', end: 'bottom top', scrub: true }
      });
    });
    gsap.utils.toArray('[data-float]').forEach(function (el, i) {
      var dx = (i % 2 ? 1 : -1) * (8 + (i % 4) * 5);
      var dy = 10 + (i % 3) * 7;
      var rot = (i % 2 ? 1 : -1) * (5 + (i % 3) * 4);
      var dur = 4.5 + (i % 5) * 0.8;
      gsap.to(el, { x: dx, y: dy, rotation: rot, duration: dur, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: (i % 6) * 0.25 });
    });
    if (window.matchMedia && window.matchMedia('(pointer: fine)').matches) {
      var movers = gsap.utils.toArray('[data-mouse]');
      if (movers.length) {
        var qs = movers.map(function (el) {
          return {
            x: gsap.quickTo(el, 'x', { duration: 0.7, ease: 'power3' }),
            y: gsap.quickTo(el, 'y', { duration: 0.7, ease: 'power3' }),
            f: parseFloat(el.getAttribute('data-mouse')) || 0.12
          };
        });
        window.addEventListener('mousemove', function (e) {
          var dx = e.clientX / window.innerWidth - 0.5;
          var dy = e.clientY / window.innerHeight - 0.5;
          for (var i = 0; i < qs.length; i++) { qs[i].x(dx * 120 * qs[i].f); qs[i].y(dy * 120 * qs[i].f); }
        }, { passive: true });
      }
    }
  }

  /* Safety net: rescue ONLY on-screen reveals (legacy + data-gsap on-scroll)
     if they never fired — never leaves visible content stuck hidden. */
  setTimeout(function () {
    var vh = window.innerHeight;
    function rescue(el, extra) {
      var top = el.getBoundingClientRect().top;
      if (top < vh * 0.92 && parseFloat(getComputedStyle(el).opacity) < 0.05) {
        gsap.to(el, Object.assign({ opacity: 1, duration: 0.3 }, extra || {}));
      }
    }
    reveals.forEach(function (el) { rescue(el, { y: 0, scale: 1 }); });
    onScrollEls.forEach(function (el) {
      // For stagger containers the container stays opacity:1 (its CHILDREN are
      // hidden), so probe a child instead — otherwise a never-fired grid stays blank.
      if (el.hasAttribute('data-gsap-stagger-on-scroll')) {
        var kids = el.children;
        var top = el.getBoundingClientRect().top;
        if (kids.length && top < vh * 0.92 && parseFloat(getComputedStyle(kids[0]).opacity) < 0.05) {
          gsap.to(kids, { opacity: 1, y: 0, duration: 0.3, stagger: 0.03 });
        }
      } else {
        rescue(el);
      }
    });
    ST.refresh();
  }, 1600);

  window.addEventListener('load', function () { ST.refresh(); });
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { ST.refresh(); });
})();
